/**
 * Caption text styling.
 *
 * No platform supports rich text in a caption — Instagram, Facebook, LinkedIn,
 * X and Google Business all store it as a plain string. The universal
 * workaround (what every scheduling tool does) is to substitute look-alike
 * glyphs from Unicode's Mathematical Alphanumeric Symbols block, so the
 * characters *are* the formatting and survive any copy/paste.
 *
 * Worth knowing before leaning on it: screen readers announce these as loose
 * symbols and search doesn't match them, so they suit a headline or one key
 * phrase — not a whole caption.
 */

export type TextStyle = 'bold' | 'italic' | 'boldItalic' | 'strike';

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS = '0123456789';

/** Combining long stroke overlay — renders as a line through the previous glyph. */
const STRIKE_MARK = '\u0336';

/** Codepoint of the styled 'A' / 'a' / '0' for each mapped style. */
const RANGES: Record<
  Exclude<TextStyle, 'strike'>,
  { upper: number; lower: number; digits?: number }
> = {
  bold: { upper: 0x1d5d4, lower: 0x1d5ee, digits: 0x1d7ec },
  italic: { upper: 0x1d608, lower: 0x1d622 }, // no italic digits exist
  boldItalic: { upper: 0x1d63c, lower: 0x1d656 },
};

const forward = new Map<TextStyle, Map<string, string>>();
/** Every styled glyph back to its plain character, across all styles. */
const reverse = new Map<string, string>();

for (const [style, range] of Object.entries(RANGES)) {
  const map = new Map<string, string>();
  const add = (plain: string, base: number) => {
    [...plain].forEach((ch, i) => {
      const styled = String.fromCodePoint(base + i);
      map.set(ch, styled);
      reverse.set(styled, ch);
    });
  };
  add(UPPER, range.upper);
  add(LOWER, range.lower);
  if (range.digits) add(DIGITS, range.digits);
  forward.set(style as TextStyle, map);
}

/** Strip every style back to ordinary characters. */
export function toPlain(text: string): string {
  return [...text.split(STRIKE_MARK).join('')].map((ch) => reverse.get(ch) ?? ch).join('');
}

/** Re-style from scratch, so styles replace rather than stack. */
export function applyStyle(text: string, style: TextStyle): string {
  const plain = toPlain(text);
  if (style === 'strike') return [...plain].map((ch) => ch + STRIKE_MARK).join('');
  const map = forward.get(style)!;
  return [...plain].map((ch) => map.get(ch) ?? ch).join('');
}

/** True when the run is already fully in this style (so the button un-styles). */
export function hasStyle(text: string, style: TextStyle): boolean {
  const plain = toPlain(text);
  if (!/[a-z0-9]/i.test(plain)) return false;
  return applyStyle(text, style) === text;
}

export function toggleStyle(text: string, style: TextStyle): string {
  return hasStyle(text, style) ? toPlain(text) : applyStyle(text, style);
}
